import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  input,
  signal,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideCopy } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { CodeLine, CodeToken, snippetText } from '../../embed-snippet';

/** Copy feedback: idle, just copied, or the clipboard refused (e.g. no permission). */
type CopyState = 'idle' | 'copied' | 'failed';

const TOKEN_CLASSES: Record<CodeToken['kind'], string> = {
  tag: 'text-sky-700 dark:text-sky-400',
  attr: 'text-violet-700 dark:text-violet-400',
  value: 'text-emerald-700 dark:text-emerald-400',
  plain: '',
};

/**
 * A highlighted, line-numbered code snippet with a copy button. The copied text
 * is built from the same lines that are shown, so both can never drift apart.
 */
@Component({
  selector: 'app-code-block',
  imports: [NgIcon, HlmButtonImports],
  providers: [provideIcons({ lucideCheck, lucideCopy })],
  templateUrl: './code-block.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodeBlockComponent implements OnDestroy {
  readonly lines = input.required<CodeLine[]>();
  readonly title = input('Code');

  protected readonly copyState = signal<CopyState>('idle');
  protected readonly text = computed(() => snippetText(this.lines()));
  protected readonly tokenClasses = TOKEN_CLASSES;

  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  protected async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.text());
      this.showState('copied');
    } catch {
      this.showState('failed');
    }
  }

  private showState(state: CopyState): void {
    if (this.resetTimer) clearTimeout(this.resetTimer);
    this.copyState.set(state);
    this.resetTimer = setTimeout(
      () => this.copyState.set('idle'),
      state === 'copied' ? 2000 : 6000,
    );
  }

  ngOnDestroy(): void {
    if (this.resetTimer) clearTimeout(this.resetTimer);
  }
}
